/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useRef } from "react";
import { Sheet } from "../../../../components/ui/sheet";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { setBasicDialogOpenFor } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { IGeoRegion } from "../../../../store/api/admin/types";
import { initialGeoRegion } from "../../../../components/helpers";

interface IAddRegion {
  selectedRegion: IGeoRegion;
  setSelectedRegion: (region: IGeoRegion) => void;
  saveButtonEnabled: boolean;
  setSaveButtonEnabled: (enabled: boolean) => void;
  errMsg: { [key: string]: string };
  onNameChange: (event: any) => void;
  onNameBlur: (event: any) => void;
  onSubmit: (event: any) => void;
}

const AddRegion: React.FC<IAddRegion> = ({
  selectedRegion,
  setSelectedRegion,
  saveButtonEnabled,
  setSaveButtonEnabled,
  errMsg,
  onNameChange,
  onNameBlur,
  onSubmit,
}) => {
  const dispatch = useStoreDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelectedRegion(initialGeoRegion);
    setSaveButtonEnabled(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [setSaveButtonEnabled, setSelectedRegion]);

  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const footer = (
    <>
      <Button
        variant="outline"
        onClick={handleClose}
      >
        Cancel
      </Button>
      <Button
        disabled={!saveButtonEnabled}
        onClick={() => {
          onSubmit(handleClose);
        }}
      >
        Save
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title="New Region" footer={footer}>
      <form autoComplete="off">
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="region-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="region-name"
              name="name"
              autoFocus
              value={selectedRegion.name}
              onChange={onNameChange}
              onBlur={onNameBlur}
              maxLength={50}
              placeholder="Enter region name"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.code === "Enter") {
                  event.preventDefault();
                  onSubmit(handleClose);
                }
              }}
              tooltipText="Maximum length: 50 characters"
            />
            {errMsg?.name && (
              <p className="text-sm text-red-500">{errMsg.name}</p>
            )}
          </div>
        </div>
      </form>
    </Sheet>
  );
};

export default AddRegion;
